import { Image as ImageIcon, Play } from 'lucide-react-native';
import { useFocusEffect } from 'expo-router';
import { Component, lazy, Suspense, useCallback, useState, type ReactNode } from 'react';
import { Image, Pressable, ScrollView, StyleSheet, View } from 'react-native';

import { Text } from '@/components/Text';
import { colors, fontSizes, radii, spacing } from '@/constants/theme';
import { canPlayInline } from '@/lib/inlineVideo';
import { openWebUrl } from '@/lib/openWebUrl';
import type { SocialDealCopy } from '@/types/deal';

export type SocialExampleVideosProps = {
  videos: NonNullable<SocialDealCopy['example_videos']>;
  testID?: string;
};

// Loaded only when a tile starts playing, which only happens where
// canPlayInline() is true: importing expo-video on an older build throws.
const ExampleVideoPlayer = lazy(() => import('./ExampleVideoPlayer'));

// A web chunk that fails to load (offline, or a tab left open across a
// deploy) would otherwise take the whole deal screen down with it.
class PlayerBoundary extends Component<{ onError: () => void; children: ReactNode }, { failed: boolean }> {
  override state = { failed: false };

  static getDerivedStateFromError() {
    return { failed: true };
  }

  override componentDidCatch() {
    this.props.onError();
  }

  override render() {
    return this.state.failed ? null : this.props.children;
  }
}

/**
 * "Example videos": a sideways row of 9:16 tiles. A tile with a url plays
 * it in place (one at a time) where the app can, or opens it in the browser
 * on builds without the player; it sits on the video's own thumbnail when
 * the server has one. A tile without a url is the server's placeholder
 * until real examples exist.
 */
export function SocialExampleVideos({ videos, testID }: SocialExampleVideosProps) {
  const [playing, setPlaying] = useState<number | null>(null);
  const inline = canPlayInline();
  // Leaving the screen (a pushed step, the order sheet) keeps this mounted;
  // dropping the player stops the video instead of playing on underneath.
  useFocusEffect(useCallback(() => () => setPlaying(null), []));
  return (
    <ScrollView
      horizontal
      showsHorizontalScrollIndicator={false}
      contentContainerStyle={styles.row}
      style={styles.scroller}
      testID={testID}
    >
      {videos.map((video, i) => {
        const { url, thumbnail_url: thumbnail } = video;
        const tileTestID = testID ? `${testID}-${i}` : undefined;
        if (url && playing === i) {
          return (
            <View key={i} style={styles.tile} testID={tileTestID}>
              <PlayerBoundary
                onError={() => {
                  setPlaying(null);
                  openWebUrl(url);
                }}
              >
                <Suspense fallback={null}>
                  <ExampleVideoPlayer
                    url={url}
                    title={video.title}
                    testID={tileTestID ? `${tileTestID}-player` : undefined}
                  />
                </Suspense>
              </PlayerBoundary>
            </View>
          );
        }
        const Icon = url ? Play : ImageIcon;
        const onThumbnail = !!thumbnail;
        const play = url ? () => (inline ? setPlaying(i) : openWebUrl(url)) : undefined;
        return (
          <Pressable
            key={i}
            onPress={play}
            disabled={!url}
            accessibilityRole={url ? (inline ? 'button' : 'link') : undefined}
            accessibilityLabel={video.title}
            accessibilityHint={url ? (inline ? 'Plays the video' : 'Opens the video') : undefined}
            style={({ pressed }) => [styles.tile, pressed && styles.pressed]}
            testID={tileTestID}
          >
            {thumbnail ? (
              <Image
                source={{ uri: thumbnail }}
                style={styles.thumbnail}
                resizeMode="cover"
                testID={tileTestID ? `${tileTestID}-thumbnail` : undefined}
              />
            ) : null}
            <View style={[styles.icon, onThumbnail && styles.play]}>
              <Icon size={onThumbnail ? 28 : 22} color={colors.frost} strokeWidth={2} />
            </View>
            {/* A video's own frame says what it is; only a placeholder needs its words. */}
            {onThumbnail ? null : (
              <Text variant="caption" color="frost" align="center" numberOfLines={2} style={styles.label}>
                {video.title}
              </Text>
            )}
          </Pressable>
        );
      })}
    </ScrollView>
  );
}

// Figma "Apply" frame: 156 x 240 tiles, two and a peek of the third across the page.
const TILE_WIDTH = 156;
const ICON_CIRCLE = 44;
const PLAY_CIRCLE = 60;

const styles = StyleSheet.create({
  // Bleeds to the screen edge so the row reads as scrollable.
  scroller: {
    marginHorizontal: -spacing.lg,
  },
  row: {
    gap: spacing.sm,
    paddingHorizontal: spacing.lg,
  },
  tile: {
    width: TILE_WIDTH,
    aspectRatio: 156 / 240,
    alignItems: 'center',
    justifyContent: 'center',
    gap: spacing.sm,
    padding: spacing.md,
    borderRadius: radii.lg,
    backgroundColor: colors.deepGreen,
    overflow: 'hidden',
  },
  thumbnail: {
    ...StyleSheet.absoluteFillObject,
  },
  icon: {
    width: ICON_CIRCLE,
    height: ICON_CIRCLE,
    borderRadius: radii.full,
    alignItems: 'center',
    justifyContent: 'center',
  },
  // Over a thumbnail: a bigger play button on the same dim the login screen
  // uses on its photo, so it reads on any frame.
  play: {
    width: PLAY_CIRCLE,
    height: PLAY_CIRCLE,
    backgroundColor: colors.deepGreenOverlay,
  },
  label: {
    fontSize: fontSizes.xs,
    lineHeight: 16,
  },
  pressed: {
    opacity: 0.8,
  },
});
